import React from "react";
import InformTable from "../Components/InformTable";
import { checkIfEncrypted, formatDateIfValid } from "../utils/dateHelpers";
import { HistoryAction } from "../HistoryActionModel";
import {
    buildValuesTableData,
    buildDateTableData,
    buildModifiedTableData,
} from "../utils/historyActionHelpers";

const formatUserValue = (property: string, value: any): string | null => {
    if (value === null || value === undefined) return null;
    if (property === "Password") return checkIfEncrypted(String(value));
    if (property === "isActive") return value ? "Active" : "Not Active";
    return String(value);
};

export const UserCreated: React.FC<{ action: HistoryAction }> = ({
    action,
}) => {
    const general = buildValuesTableData(
        action.Changes,
        "new",
        formatUserValue,
    );
    const dates = buildDateTableData(action.Changes, "new");

    return (
        <div className="inform_panel">
            <InformTable data={general} label="User Details" />
            {dates.length > 0 && <InformTable data={dates} label="Date" />}
        </div>
    );
};

export const UserDeleted: React.FC<{ action: HistoryAction }> = ({
    action,
}) => {
    const general = buildValuesTableData(
        action.Changes,
        "old",
        formatUserValue,
    );
    const dates = buildDateTableData(action.Changes, "old");

    return (
        <div className="inform_panel">
            <InformTable data={general} label="General" />
            {dates.length > 0 && <InformTable data={dates} label="Date" />}
        </div>
    );
};

export const UserUpdated: React.FC<{ action: HistoryAction }> = ({
    action,
}) => {
    const { dataOld, dataNew } = buildModifiedTableData(
        action.Changes,
        formatUserValue,
    );

    return (
        <div className="inform_panel">
            <InformTable data={dataOld} label="Old Version" />
            <InformTable data={dataNew} label="New Version" />
        </div>
    );
};

// Legacy format: { "Role": "user", "Username": "...", "Password": "..." }
export const UserCreatedLegacy: React.FC<{ action: any }> = ({ action }) => {
    const data = [
        { property: "Username", value: action.Username ?? action.Name ?? null },
        { property: "Role", value: action.Role ?? null },
        { property: "Password", value: action.Password ? checkIfEncrypted(String(action.Password)) : null },
    ].filter((row): row is { property: string; value: string } => row.value !== null);

    return (
        <div className="inform_panel">
            <InformTable data={data} label="User Details" />
        </div>
    );
};

// Legacy format: { "Name": { "old": "...", "new": "..." }, "Role": { "old": "...", "new": "..." }, "Id": "..." }
export const UserUpdatedLegacy: React.FC<{ action: any }> = ({ action }) => {
    const exclude = ["Id"];
    const entries = Object.entries(action).filter(([key]) => !exclude.includes(key));

    const formatLegacyValue = (key: string, value: any): string => {
        if (value === null || value === undefined) return "—";
        if (key === "Password") return checkIfEncrypted(String(value));
        if (key === "isActive") return value ? "Active" : "Not Active";
        return String(value);
    };

    const dataOld = entries.map(([key, val]: [string, any]) => ({
        property: key,
        value: formatLegacyValue(key, val?.old),
    }));

    const dataNew = entries.map(([key, val]: [string, any]) => ({
        property: key,
        value: formatLegacyValue(key, val?.new),
    }));

    return (
        <div className="inform_panel">
            <InformTable data={dataOld} label="Old Version" />
            <InformTable data={dataNew} label="New Version" />
        </div>
    );
};

// Legacy format: { "Id": 1744, "Name": "...", "Password": "...", "CreateDate": unix, "isActive": true, ... }
export const UserDeletedLegacy: React.FC<{ action: any }> = ({ action }) => {
    const exclude = ["Groups", "Histories", "isAdmin"];
    const data = Object.entries(action)
        .filter(([key, val]) => !exclude.includes(key) && val !== null && val !== undefined)
        .map(([key, val]: [string, any]) => {
            let value: string;
            if (key === "Password") value = checkIfEncrypted(String(val));
            else if (key === "isActive") value = val ? "Active" : "Not Active";
            else if (key === "CreateDate") value = formatDateIfValid(val) ?? String(val);
            else value = String(val);
            return { property: key, value };
        });

    return (
        <div className="inform_panel">
            <InformTable data={data} label="General" />
        </div>
    );
};
